import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import { PRO_MODELS_TEXT } from '../../constants/aiModels';
import './ComparePages.css';

const MyaieduCompare = () => {
  const navigate = useNavigate();

  const comparisonData = [
    { category: '대상 학교급', competitor: '초등학교 전용', whatquiz: '초·중·고등학교 전체', highlight: true },
    { category: 'PPT 생성', competitor: 'Gamma 연동', whatquiz: '자체 AI PPT 엔진', highlight: false },
    { category: '학습지 생성', competitor: '활동지 생성', whatquiz: 'OX/단답형/서술형 자동 출제', highlight: false },
    { category: '생기부 작성', competitor: '세특 일부 지원', whatquiz: '전 항목 지원 (3분 완성)', highlight: true },
    { category: '수행평가 채점', competitor: '지원 안함', whatquiz: '루브릭 기반 자동 채점', highlight: true },
    { category: '이미지 생성', competitor: '지원 안함', whatquiz: '7가지 교육용 이미지 생성', highlight: true },
    { category: 'AI 모델', competitor: 'Gemini', whatquiz: 'GPT + Claude + Gemini', highlight: true },
    { category: '가격', competitor: '월 33,000~110,000원', whatquiz: '월 16,667원부터 (12개월 기준)', highlight: true },
    { category: '학교 예산 결제', competitor: '구독형', whatquiz: '에듀집 등록, 세금계산서', highlight: false },
  ];

  const faqData = [
    { q: 'MYAIEDU와 왓퀴즈, 어떤 것을 선택해야 하나요?', a: '초등학교에서 교수학습지도안 중심으로 사용하신다면 MYAIEDU도 좋은 선택입니다. 초·중·고 전체 학교급에서 생기부, PPT, 학습지, 채점, 이미지까지 올인원으로 필요하시다면 왓퀴즈가 더 적합합니다.' },
    { q: '왓퀴즈도 교육과정을 반영하나요?', a: '네, 왓퀴즈는 2026 기재요령을 포함한 교육과정을 AI에 반영합니다. 교과서 PDF를 첨부하면 해당 내용 기반으로 더 정확한 자료가 생성됩니다.' },
    { q: '왓퀴즈가 MYAIEDU보다 저렴한가요?', a: '네, 왓퀴즈 PRO는 12개월 200,000원(월 16,667원)이라 MYAIEDU 기본 플랜 월 33,000원의 절반 정도예요. 기능 범위도 왓퀴즈가 더 넓어요.' },
    { q: '무료로 체험해볼 수 있나요?', a: '네, 왓퀴즈는 가입만 하면 채팅·학습지·생기부(한 번에 3명) 등 주요 기능을 무료로 쓸 수 있어요. 카드 등록 없이 바로 시작하세요.' },
  ];

  return (
    <div className="compare-page">
      <Helmet>
        <title>MYAIEDU vs 왓퀴즈 비교 | 교사 AI 도구 어떤 게 더 나을까? (2026)</title>
        <meta name="description" content="MYAIEDU와 왓퀴즈 완벽 비교. PPT, 학습지, 생기부 작성, 가격까지. MYAIEDU는 초등 전용, 왓퀴즈는 초중고 올인원. 교사에게 더 맞는 AI 도구를 비교합니다." />
        <meta name="keywords" content="MYAIEDU 대안, MYAIEDU vs 왓퀴즈, 초등 AI 도구, 교사 AI 비교, 수업자료 AI 비교, MYAIEDU 가격, 교사 PPT AI" />
        <link rel="canonical" href="https://whatquiz.co.kr/compare/myaiedu" />
        <meta property="og:title" content="MYAIEDU vs 왓퀴즈 | 교사 AI 도구 비교" />
        <meta property="og:description" content="MYAIEDU와 왓퀴즈, 어떤 AI 도구가 교사에게 더 맞을까? 기능, 가격, 대상 학교급 비교." />
        <meta property="og:url" content="https://whatquiz.co.kr/compare/myaiedu" />
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) })}
        </script>
      </Helmet>

      <section className="compare-hero">
        <div className="compare-hero-inner">
          <p className="compare-hero-label">2026년 최신 비교</p>
          <h1 className="compare-hero-title">MYAIEDU vs 왓퀴즈<br /><span className="compare-hero-highlight">교사에게 더 맞는 AI는?</span></h1>
          <p className="compare-hero-desc">초등 전용 MYAIEDU와 초·중·고 올인원 왓퀴즈,<br />기능, 가격, 대상을 솔직하게 비교해드립니다.</p>
        </div>
      </section>

      <section className="compare-tldr">
        <div className="compare-tldr-inner">
          <h2 className="compare-section-title">한눈에 보는 결론</h2>
          <div className="compare-tldr-cards">
            <div className="compare-tldr-card">
              <h3>MYAIEDU가 더 나은 경우</h3>
              <ul>
                <li>초등학교 교사로서 초등 교육과정에 특화된 도구를 원할 때</li>
                <li>교수학습지도안 생성이 주요 용도일 때</li>
                <li>웹 편집기에서 PPT를 바로 수정하고 싶을 때</li>
              </ul>
            </div>
            <div className="compare-tldr-card compare-tldr-card--primary">
              <h3>왓퀴즈가 더 나은 경우</h3>
              <ul>
                <li>초·중·고등학교 모든 학교급에서 사용하고 싶을 때</li>
                <li>생기부, PPT, 학습지, 채점, 이미지까지 한 곳에서 해결하고 싶을 때</li>
                <li>GPT, Claude, Gemini 등 여러 AI 모델을 쓰고 싶을 때</li>
                <li>합리적인 가격이 중요할 때 (월 16,667원부터)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="compare-table-section">
        <div className="compare-table-inner">
          <h2 className="compare-section-title">상세 기능 비교</h2>
          <div className="compare-table-wrapper">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>비교 항목</th>
                  <th>MYAIEDU</th>
                  <th className="compare-table-highlight">왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className={row.highlight ? 'compare-table-row--highlight' : ''}>
                    <td className="compare-table-category">{row.category}</td>
                    <td>{row.competitor}</td>
                    <td className="compare-table-whatquiz">{row.whatquiz}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="compare-reasons">
        <div className="compare-reasons-inner">
          <h2 className="compare-section-title">왓퀴즈만의 차별점</h2>
          <div className="compare-reasons-grid">
            {[
              { title: '초중고 전체 지원', desc: 'MYAIEDU는 초등학교에 특화되어 있습니다. 왓퀴즈는 초·중·고등학교 모든 학교급의 교사가 사용할 수 있어, 학교를 옮기더라도 계속 사용할 수 있습니다.' },
              { title: '올인원 기능', desc: '왓퀴즈는 PPT, 학습지 외에 생기부 전 항목 자동 작성, 수행평가 자동 채점, AI 이미지 생성, 교사 AI 상담까지 모든 교사 업무를 커버합니다.' },
              { title: '약 절반 가격', desc: '왓퀴즈 PRO 12개월은 200,000원(월 16,667원)으로, MYAIEDU 기본 월 33,000원의 절반 정도예요. 기능은 더 많고 가격은 더 가벼워요.' },
              { title: '멀티 AI 모델', desc: `MYAIEDU는 Gemini만 사용해요. 왓퀴즈는 ${PRO_MODELS_TEXT} 중 용도에 맞게 선택할 수 있어요.` },
            ].map((r, idx) => (
              <div key={idx} className="compare-reason-card">
                <h3>{r.title}</h3>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="compare-faq">
        <div className="compare-faq-inner">
          <h2 className="compare-section-title">자주 묻는 질문</h2>
          <div className="compare-faq-list">
            {faqData.map((faq, idx) => (
              <div key={idx} className="compare-faq-item">
                <h3 className="compare-faq-q">{faq.q}</h3>
                <p className="compare-faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="compare-cta">
        <div className="compare-cta-inner">
          <h2>교사를 위해 만들어진 AI,<br />직접 경험해보세요</h2>
          <p>가입만 하면 무료로 시작할 수 있습니다</p>
          <div className="compare-cta-buttons">
            <button className="compare-cta-primary" onClick={() => navigate('/chat')}>무료로 시작하기</button>
            <button className="compare-cta-secondary" onClick={() => navigate('/pricing')}>요금제 보기</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MyaieduCompare;
